import React, { useState, useEffect } from "react";
import { Tooltip } from "./Tooltip";
import { Button } from "./Button";
import { Countdown } from "./Countdown";
import { FormattedText } from "./FormattedText";

export const EventModal = ({
  isOpen,
  onClose,
  title,
  description,
  url,
  start,
  image,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  url: string;
  start: Date;
  image: string;
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 150);
  };

  const handleGoToEvent = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!isOpen) return null;

  const now = new Date();
  const hasStarted = now >= start;

  const goButton = (
    <Button onClick={handleGoToEvent} variant="primary" grow={false}>
      Go to the event!
    </Button>
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-150 ${
        isClosing ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div
        className={`relative mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800 transition-transform duration-150 ${
          isClosing ? "scale-95" : "scale-100"
        }`}
      >
        {image && (
          <img
            src={image}
            alt={title}
            className="mb-4 h-32 w-full rounded-lg object-cover"
          />
        )}

        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
          {title}
        </h2>

        <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {hasStarted ? (
            "Event is happening now!"
          ) : (
            <>
              Starts in <Countdown detailed until={start} />
            </>
          )}
        </div>

        {description && (
          <div className="mt-4 text-slate-600 dark:text-slate-300">
            <FormattedText text={description} />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button onClick={handleClose} variant="text" grow={false}>
            Close
          </Button>
          {hasStarted ? (
            goButton
          ) : (
            <Tooltip
              element={goButton}
              tooltip="The event hasn't started yet, but you can still go and see what's going on!"
              position="top"
            />
          )}
        </div>
      </div>
    </div>
  );
};
